import { toNullableBooleanFromArray } from '@payer-admin-ui/components';
import { wrappedApiGet, wrappedApiPost, wrappedApiPut } from 'utils/http';
import { sumBy } from 'lodash';
import { ResponseInvoiceV2 } from '@payer-admin-ui/api/src/invoices/models/ResponseInvoiceV2';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import {
  BankFilePaymentResponse,
  DetailedBankFilePaymentResponse,
  GetDetailedBankFilePaymentResponse,
  Mapping,
  MappingType,
} from '@payer-admin-ui/api/src/bankfilepayments/bankFilePaymentTypes';
import { FundSourceType } from '../utils/constants';
import { BankFilePaymentFilters } from '../views/Pages/payments/bankfile/types/bankFilePaymentState';

export type FetchPageParams = {
  page: number;
  pageSize: number;
  filters: BankFilePaymentFilters;
  sort: string[];
};

export type BankFilePageResponseWithSummaries = {
  contents: BankFilePaymentResponse[];
  offset: number;
  pageSize: number;
  totalCount: number;
  summaryByCurrency?: BankFilePaymentsSumByCurrency[];
};

export type BankFilePaymentsSumByCurrency = {
  amount: number;
  mappedAmount: number;
  fundSourceType: string;
  currencyCode: CurrencyCode;
};

export type MapToInvoicesParams = {
  bankFilePaymentId: number;
  bankFilePaymentAmount: number;
  bankFilePaymentMappings: {
    fundSourceType: string;
    amount: number;
  }[];
  creditInvoiceIdsAndAmounts: {
    invoiceId: string;
    amount: string;
  }[];
  debitInvoiceIdsAndAmounts: {
    invoiceId: string;
    amount: string;
  }[];
};

export type MapToIasParams = {
  bankFilePaymentId: number;
  debitInvoiceAggregationIdsAndAmounts: {
    invoiceAggregationId: string;
    amount: string;
  }[];
};

export type SetBackofficeParams = {
  bankFilePaymentId: string;
  merchantComment: string | null;
};

export type DoNotMapParams = {
  bankFilePaymentId: string;
  reason: string;
};

export type MarkAsManuallyRefundedParams = {
  bankFilePaymentId: string;
  remainingUnmappedAmount: number;
};

export type MappingRequest = {
  amount: number;
  invoiceId?: number;
  creditInvoiceId?: number;
  mappingType?: MappingType;
};

export type ManualMapParams = {
  bankFilePaymentId: string;
  payinMappings: MappingRequest[];
  allowPartialMapping?: boolean;
};

export type IgnoreRemainingUnmappedAmountParams = {
  bankFilePaymentId: string;
  remainingUnmappedAmount: number;
};

export type MapPayinToInvoicesResponse = {
  bankFilePayment: BankFilePaymentResponse;
  performedMappings: MappingWithInvoices[];
};

export type MappingWithInvoices = {
  mappings: Mapping[];
  invoice: ResponseInvoiceV2;
  creditInvoice: ResponseInvoiceV2;
};

export type SplitAndMapPayInsToInvoicesResponse = {
  bankFilePayment: BankFilePaymentResponse;
  performedMappings: MappingWithInvoices[];
};

const bankFilePaymentsAPI = {
  bankFilePayments: {
    fetchById: async ({
      bankFilePaymentId,
    }: {
      bankFilePaymentId: string;
    }): Promise<GetDetailedBankFilePaymentResponse> =>
      wrappedApiGet<GetDetailedBankFilePaymentResponse>(
        `/payments/bank-files/${bankFilePaymentId}`,
      ).withObjectResponse(),

    fetchPage: async ({
      page,
      pageSize,
      filters: { fullyMapped, manuallyMapped, toPayer, ...restOfFilters },
      sort,
    }: FetchPageParams): Promise<BankFilePageResponseWithSummaries> => {
      return wrappedApiGet<BankFilePageResponseWithSummaries>(`/payments/bank-files`, {
        query: {
          offset: page * pageSize,
          pageSize,
          fullyMapped: toNullableBooleanFromArray(fullyMapped),
          manuallyMapped: manuallyMapped ? true : undefined,
          toPayer: toNullableBooleanFromArray(toPayer),
          ...restOfFilters,
          sort,
        },
      }).withObjectResponse();
    },

    mapToInvoices: async ({
      bankFilePaymentId,
      bankFilePaymentAmount,
      bankFilePaymentMappings,
      creditInvoiceIdsAndAmounts,
      debitInvoiceIdsAndAmounts,
    }: MapToInvoicesParams) => {
      const creditInvoiceReferences = creditInvoiceIdsAndAmounts.map(creditInvoiceIdAndAmount => {
        const { invoiceId, amount } = creditInvoiceIdAndAmount;
        return {
          // Use parseInt() so that sumBy below will sum ints instead of concatenating strings
          // and multiply with -1 to make the amount positive
          amount: parseInt(amount) * -1,
          creditInvoiceId: invoiceId,
        };
      });

      const paymentToAccountMappings = bankFilePaymentMappings.filter(mapping => {
        return mapping.fundSourceType === FundSourceType.PAYMENT_TO_ACCOUNT;
      });
      const sumExistingMappings = sumBy(paymentToAccountMappings, 'amount');

      const payments = debitInvoiceIdsAndAmounts.map(debitInvoiceIdAndAmount => {
        const { invoiceId, amount } = debitInvoiceIdAndAmount;
        return {
          // Use parseInt() so that sumBy below will sum ints instead of concatenating strings
          amount: parseInt(amount),
          invoiceId: invoiceId,
        };
      });

      const sumPayments = sumBy(payments, 'amount');
      const sumCreditInvoiceReferences = sumBy(creditInvoiceReferences, 'amount');
      const leaveUnmappedAmount =
        bankFilePaymentAmount - sumExistingMappings + sumCreditInvoiceReferences - sumPayments;

      return wrappedApiPost<MapPayinToInvoicesResponse>(`/payments/bank-files/map-payin-to-invoices`, {
        body: {
          bankFilePaymentId: bankFilePaymentId,
          funds: {
            payinToAccountAmount: bankFilePaymentAmount - sumExistingMappings,
            creditInvoiceReferences: creditInvoiceReferences,
          },
          payments: payments,
          leaveUnmappedAmount: leaveUnmappedAmount,
        },
      }).withObjectResponse();
    },

    mapToIas: async ({ bankFilePaymentId, debitInvoiceAggregationIdsAndAmounts }: MapToIasParams) => {
      const wantedMappings = debitInvoiceAggregationIdsAndAmounts.map(debitInvoiceIdAndAmount => {
        const { invoiceAggregationId, amount } = debitInvoiceIdAndAmount;
        return {
          // Use parseInt() so that sumBy below will sum ints instead of concatenating strings
          invoiceAggregationId: invoiceAggregationId,
          customAmountFromPayin: parseInt(amount),
        };
      });

      const sumWantedMappings = sumBy(wantedMappings, 'customAmountFromPayin');

      return wrappedApiPost(`/payments/bank-files/map-payin-to-ias`, {
        body: {
          bankFilePaymentId: bankFilePaymentId,
          invoiceAggregations: wantedMappings,
          customAmountFromPayin: sumWantedMappings,
        },
      }).withEmptyResponse();
    },

    setBackoffice: async ({
      bankFilePaymentId,
      merchantComment,
    }: SetBackofficeParams): Promise<GetDetailedBankFilePaymentResponse> => {
      return wrappedApiPut<GetDetailedBankFilePaymentResponse>(`/payments/bank-files/${bankFilePaymentId}/backoffice`, {
        body: {
          comment: merchantComment,
        },
      }).withObjectResponse();
    },

    doNotMap: async ({ bankFilePaymentId, reason }: DoNotMapParams): Promise<DetailedBankFilePaymentResponse> => {
      return wrappedApiPut<DetailedBankFilePaymentResponse>(`/payments/bank-files/${bankFilePaymentId}/do-not-map`, {
        query: {
          reason: reason,
        },
      }).withObjectResponse();
    },

    manualMap: async ({
      bankFilePaymentId,
      payinMappings,
      allowPartialMapping,
    }: ManualMapParams): Promise<SplitAndMapPayInsToInvoicesResponse> => {
      return wrappedApiPost<SplitAndMapPayInsToInvoicesResponse>(`/payments/bank-files/split-map`, {
        body: {
          bankFilePaymentId,
          payinMappings,
          allowPartialMapping,
        },
      }).withObjectResponse();
    },

    markAsManuallyRefunded: async ({
      bankFilePaymentId,
      remainingUnmappedAmount,
    }: MarkAsManuallyRefundedParams): Promise<SplitAndMapPayInsToInvoicesResponse> => {
      return wrappedApiPost<SplitAndMapPayInsToInvoicesResponse>(`/payments/bank-files/split-map`, {
        body: {
          bankFilePaymentId: bankFilePaymentId,
          payinMappings: {
            amount: remainingUnmappedAmount,
            mappingType: 'MANUAL_REFUND',
          },
        },
      }).withObjectResponse();
    },

    ignoreRemainingUnmappedAmount: async ({
      bankFilePaymentId,
      remainingUnmappedAmount,
    }: IgnoreRemainingUnmappedAmountParams): Promise<SplitAndMapPayInsToInvoicesResponse> => {
      return wrappedApiPost<SplitAndMapPayInsToInvoicesResponse>(`/payments/bank-files/split-map`, {
        body: {
          bankFilePaymentId: bankFilePaymentId,
          payinMappings: {
            amount: remainingUnmappedAmount,
            mappingType: 'IGNORED',
          },
        },
      }).withObjectResponse();
    },
  },
};

export { bankFilePaymentsAPI };
